import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Crown, DoorOpen, KeyRound, Lock, PlusCircle, Spade, Sparkles, Users, X } from 'lucide-react';
import api from 'lib/api';
import { navigateInApp } from 'lib/navigation';
import { API_ENDPOINTS } from 'constants/game';
import { PrivateRoomConfig, PrivateRoomInfo } from 'types/game';
import { useAuth } from 'context/AuthProvider';
import { formatCurrencyAmount } from 'lib/currency-format';
import { buyInBlockMessage } from 'lib/buy-in-messages';
import PrivateRoomCodeActions from 'components/lobby/PrivateRoomCodeActions';
import { privateRoomToBuyInInfo } from 'lib/private-room-buy-in';
import { privateRoomJoinErrorMessage } from 'lib/private-room-messages';
import {
  VARIANT_MINI_CARDS,
  formatVariantLabel,
  variantCodesMatch,
  variantTheme,
} from 'lib/format-variant';

type PrivateTab = 'join' | 'create';

interface CreatedPrivateRoom {
  roomCode: string;
  variantCode: string;
  variantName: string;
  bootAmount: number;
  tableName?: string;
}

interface PrivateRoomModalProps {
  open: boolean;
  variantCode: string;
  initialJoinCode?: string;
  autoJoin?: boolean;
  onClose: () => void;
  onError?: (message: string) => void;
}

function DecoCard({ label, offset }: { label: string; offset: number }) {
  const isRed = label.includes('♥') || label.includes('♦');
  const rank = label.replace(/[♥♠♣♦]/g, '');
  const suit = label.match(/[♥♠♣♦]/)?.[0] ?? '♠';
  return (
    <div
      className={`private-room-modal__mini-card ${
        isRed ? 'private-room-modal__mini-card--red' : 'private-room-modal__mini-card--black'
      }`}
      style={{
        left: offset,
        transform: `rotate(${-10 + offset * 0.12}deg)`,
        zIndex: offset,
      }}
      aria-hidden
    >
      <span>{rank}</span>
      <span className="text-[10px]">{suit}</span>
    </div>
  );
}

function CardFan({ labels }: { labels: string[] }) {
  const fanWidth = 52 + Math.max(0, labels.length - 1) * 28;
  return (
    <div className="private-room-modal__card-fan" aria-hidden>
      <div className="private-room-modal__card-fan-inner" style={{ width: fanWidth, height: 88 }}>
        {labels.map((label, i) => (
          <DecoCard key={`${label}-${i}`} label={label} offset={i * 28} />
        ))}
      </div>
    </div>
  );
}

export default function PrivateRoomModal({
  open,
  variantCode,
  initialJoinCode = '',
  autoJoin = false,
  onClose,
  onError,
}: PrivateRoomModalProps) {
  const navigate = useNavigate();
  const { user, refreshSession } = useAuth();
  const lobbyVariant = formatVariantLabel(variantCode);
  const theme = variantTheme(variantCode);
  const miniCards = VARIANT_MINI_CARDS[(variantCode || 'classic').toLowerCase()] || VARIANT_MINI_CARDS.classic;
  const [tab, setTab] = useState<PrivateTab>('join');
  const [config, setConfig] = useState<PrivateRoomConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [localError, setLocalError] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [creating, setCreating] = useState(false);
  const [bootAmount, setBootAmount] = useState('');
  const [roomName, setRoomName] = useState('');
  const [createdRoom, setCreatedRoom] = useState<CreatedPrivateRoom | null>(null);
  const [enteringCreated, setEnteringCreated] = useState(false);
  const autoJoinedRef = useRef(false);

  const reportError = useCallback(
    (message: string) => {
      setLocalError(message);
      onError?.(message);
    },
    [onError]
  );

  useEffect(() => {
    if (!open) {
      setLocalError('');
      setCreatedRoom(null);
      onError?.('');
      return;
    }

    autoJoinedRef.current = false;
    setLocalError('');
    setCreatedRoom(null);
    onError?.('');

    if (autoJoin && initialJoinCode.trim()) {
      setJoinCode(initialJoinCode);
      setTab('join');
    } else {
      setJoinCode('');
      setRoomName('');
      setBootAmount('');
      setTab('join');
    }
  }, [open, autoJoin, initialJoinCode, onError]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get<PrivateRoomConfig>(API_ENDPOINTS.FRONTEND.PRIVATE_ROOMS_CONFIG).then((res) => {
      setLoading(false);
      if (res.success && res.data) {
        setConfig(res.data);
        const min = res.data.privateTableMinAmount;
        if (min != null) {
          setBootAmount((current) => (current.trim() ? current : String(min)));
        }
      }
    });
  }, [open]);

  const lobbyVariantMeta = useMemo(() => {
    const match = config?.allowedVariants.find((v) => variantCodesMatch(v.code, variantCode));
    return {
      id: match?.id,
      code: match?.code || lobbyVariant,
      name: match?.name || lobbyVariant,
      allowed: Boolean(match),
    };
  }, [config, variantCode, lobbyVariant]);

  const bootPicks = useMemo(() => {
    const min = Number(config?.privateTableMinAmount) || 100;
    const max = Number(config?.privateTableMaxAmount) || min * 20;
    const raw = [min, min * 2, min * 5, max];
    const seen = new Set<number>();
    return raw
      .map((v) => Math.floor(v))
      .filter((v) => v >= min && v <= max && !seen.has(v) && (seen.add(v), true))
      .slice(0, 4);
  }, [config]);

  const openJoinFlow = useCallback(
    async (code: string) => {
      setLocalError('');
      onError?.('');
      const normalized = code.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
      if (!normalized) {
        reportError('Enter a valid room code');
        return false;
      }
      if (refreshSession) {
        await refreshSession();
      }
      const res = await api.get<PrivateRoomInfo>(API_ENDPOINTS.FRONTEND.PRIVATE_ROOM(normalized));
      if (!res.success || !res.data) {
        reportError(privateRoomJoinErrorMessage(res.message));
        return false;
      }
      if (!variantCodesMatch(res.data.variantCode, variantCode)) {
        const roomVariant = formatVariantLabel(res.data.variantName || res.data.variantCode);
        reportError(
          `This room is ${roomVariant} Teen Patti. Switch to the ${roomVariant} lobby or enter a ${lobbyVariant} room code.`
        );
        return false;
      }
      if (!res.data.canJoin) {
        reportError(buyInBlockMessage(privateRoomToBuyInInfo(res.data)));
        return false;
      }
      onClose();
      navigateInApp(navigate, `/private/${normalized}`, {
        state: { variantCode: res.data.variantCode },
      });
      return true;
    },
    [variantCode, lobbyVariant, onError, onClose, navigate, refreshSession, reportError]
  );

  useEffect(() => {
    if (!open || !autoJoin || !initialJoinCode.trim() || autoJoinedRef.current) return;
    autoJoinedRef.current = true;
    void openJoinFlow(initialJoinCode);
  }, [open, autoJoin, initialJoinCode, openJoinFlow]);

  const createRoom = async () => {
    if (!user) {
      reportError('Sign in to create a private room');
      return;
    }
    if (!lobbyVariantMeta.allowed) {
      reportError(`${lobbyVariant} private rooms are not available right now`);
      return;
    }
    setLocalError('');
    onError?.('');
    setCreating(true);
    try {
      const amount = Number(bootAmount);
      if (!Number.isFinite(amount) || amount <= 0) {
        reportError('Enter a valid boot amount');
        return;
      }
      if (config?.privateTableMinAmount != null && amount < config.privateTableMinAmount) {
        reportError(`Boot amount must be at least ${formatCurrencyAmount('', config.privateTableMinAmount)}`);
        return;
      }
      if (config?.privateTableMaxAmount != null && amount > config.privateTableMaxAmount) {
        reportError(`Boot amount cannot exceed ${formatCurrencyAmount('', config.privateTableMaxAmount)}`);
        return;
      }
      const res = await api.post<{
        roomCode: string;
        name?: string | null;
        variantCode: string;
        variantName: string;
        variantId: number;
      }>(API_ENDPOINTS.FRONTEND.PRIVATE_ROOMS_CREATE, {
        name: roomName || undefined,
        variantCode: lobbyVariantMeta.code,
        bootAmount: amount,
        maxSeats: 5,
      });
      if (!res.success || !res.data?.roomCode) {
        reportError(res.message || 'Could not create room');
        return;
      }
      setCreatedRoom({
        roomCode: res.data.roomCode,
        variantCode: res.data.variantCode,
        variantName: res.data.variantName,
        bootAmount: amount,
        tableName: roomName.trim() || res.data.name || undefined,
      });
    } finally {
      setCreating(false);
    }
  };

  const enterCreatedRoom = async () => {
    if (!createdRoom || enteringCreated) return;
    setEnteringCreated(true);
    try {
      await openJoinFlow(createdRoom.roomCode);
    } finally {
      setEnteringCreated(false);
    }
  };

  if (!open) return null;

  const showingCreated = Boolean(createdRoom);
  const ChipIcon = theme === 'purple' ? Crown : Spade;

  return (
    <>
      {open &&
        createPortal(
          <div
            className="private-room-overlay provider-login-overlay fixed inset-0 z-[90] flex items-end justify-center p-0 sm:items-center sm:p-4"
            role="dialog"
            aria-modal
            aria-labelledby="private-room-title"
          >
            <button type="button" className="absolute inset-0 bg-black/85" aria-label="Close" onClick={onClose} />
            <div className={`private-room-modal private-room-modal--${theme} relative z-10 flex max-h-[min(92dvh,760px)] w-full flex-col sm:max-w-md`}>
              <button
                type="button"
                onClick={onClose}
                className="private-room-modal__close"
                aria-label="Close private room"
              >
                <X size={18} />
              </button>

              <div className="private-room-modal__hero shrink-0">
                <div className="private-room-modal__hero-glow" aria-hidden />
                <div className="private-room-modal__felt" aria-hidden />

                <div className="private-room-modal__hero-main">
                  <div className="private-room-modal__lock-badge shrink-0">
                    <Lock size={22} strokeWidth={2.25} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.35em] text-tp-gold/75">
                      <Sparkles size={11} className="text-tp-gold/60" />
                      Play with friends
                    </p>
                    <h2 id="private-room-title" className="mt-1 font-display text-xl font-bold tracking-wide text-white sm:text-2xl">
                      {showingCreated ? 'Room created' : 'Private room'}
                    </h2>
                    <p className="mt-1 text-xs leading-relaxed text-white/45">
                      {showingCreated
                        ? 'Share the code with friends, then enter when you are ready'
                        : "Host with a code or jump into a friend's invite"}
                    </p>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <span className="private-room-modal__variant-pill">
                        <ChipIcon size={11} fill="currentColor" />
                        {lobbyVariantMeta.name}
                      </span>
                      {lobbyVariantMeta.id != null && (
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-white/30">
                          Variant #{lobbyVariantMeta.id}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <CardFan labels={miniCards} />
              </div>

              <div className="private-room-modal__body flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain px-5 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-4 sm:px-6">
                {!showingCreated && (
                  <div className="private-room-modal__tabs" role="tablist">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={tab === 'join'}
                      onClick={() => setTab('join')}
                      className={`private-room-modal__tab ${tab === 'join' ? 'private-room-modal__tab--active' : ''}`}
                    >
                      <KeyRound size={14} />
                      Join
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={tab === 'create'}
                      onClick={() => setTab('create')}
                      className={`private-room-modal__tab ${tab === 'create' ? 'private-room-modal__tab--active' : ''}`}
                    >
                      <PlusCircle size={14} />
                      Create
                    </button>
                  </div>
                )}

                {localError && (
                  <p className="mt-4 rounded-xl border border-red-500/35 bg-red-950/50 px-3 py-2.5 text-sm text-red-200">
                    {localError}
                  </p>
                )}

                {!loading && !lobbyVariantMeta.allowed && !showingCreated && (
                  <p className="mt-4 rounded-xl border border-amber-500/30 bg-amber-950/40 px-3 py-2.5 text-sm text-amber-100/90">
                    Private rooms for {lobbyVariant} are disabled right now.
                  </p>
                )}

                {showingCreated && createdRoom ? (
                  <div className="private-room-modal__panel private-room-modal__panel--created">
                    <p className="text-sm font-semibold text-white">Your room code</p>
                    <p className="mt-1 text-xs text-white/45">
                      Friends join from the {formatVariantLabel(createdRoom.variantCode)} lobby with this code.
                    </p>
                    <PrivateRoomCodeActions
                      roomCode={createdRoom.roomCode}
                      variantCode={createdRoom.variantCode}
                      tableName={createdRoom.tableName}
                      size="large"
                    />
                    <dl className="private-room-modal__room-meta">
                      <div className="private-room-modal__room-meta-item">
                        <dt>Variant</dt>
                        <dd title={createdRoom.variantName}>{createdRoom.variantName}</dd>
                      </div>
                      <div className="private-room-modal__room-meta-item private-room-modal__room-meta-item--boot">
                        <dt>Boot</dt>
                        <dd>{formatCurrencyAmount('', createdRoom.bootAmount)}</dd>
                      </div>
                    </dl>
                    <button
                      type="button"
                      className="provider-play-btn mt-5 w-full"
                      disabled={enteringCreated}
                      onClick={() => void enterCreatedRoom()}
                    >
                      <DoorOpen size={16} />
                      {enteringCreated ? 'Joining…' : 'Enter room'}
                    </button>
                    <button
                      type="button"
                      className="mt-3 w-full text-center text-xs font-semibold text-white/45 transition hover:text-white/70"
                      onClick={() => setCreatedRoom(null)}
                    >
                      Back to create form
                    </button>
                  </div>
                ) : tab === 'join' ? (
                  <div className="private-room-modal__panel">
                    <p className="text-sm font-semibold text-white">Enter room code</p>
                    <p className="mt-1 text-xs text-white/40">
                      Share codes with friends — they must join from the same variant lobby.
                    </p>
                    <div className="private-room-modal__code-wrap">
                      <input
                        value={joinCode}
                        onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                        placeholder="AB12CD"
                        maxLength={10}
                        autoComplete="off"
                        spellCheck={false}
                        className="private-room-modal__code-input"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') void openJoinFlow(joinCode);
                        }}
                      />
                    </div>
                    <button
                      type="button"
                      className="provider-play-btn mt-5 w-full"
                      disabled={!lobbyVariantMeta.allowed || loading}
                      onClick={() => void openJoinFlow(joinCode)}
                    >
                      <DoorOpen size={16} />
                      Join room
                    </button>
                  </div>
                ) : (
                  <div className="private-room-modal__panel">
                    <p className="text-sm font-semibold text-white">Set up your table</p>
                    <p className="mt-1 text-xs text-white/40">
                      Room saves as {lobbyVariant} — you&apos;ll get a shareable code before entering.
                    </p>
                    {!user && (
                      <p className="mt-3 rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-xs text-white/55">
                        Sign in from Home to host a private table.
                      </p>
                    )}
                    <label className="mt-4 block text-[10px] font-bold uppercase tracking-wider text-white/40">
                      Table name <span className="font-normal normal-case text-white/25">(optional)</span>
                    </label>
                    <input
                      value={roomName}
                      onChange={(e) => setRoomName(e.target.value)}
                      className="private-room-modal__field-input mt-1.5 w-full"
                      placeholder="Friday Night Patti"
                      disabled={!lobbyVariantMeta.allowed}
                    />
                    <label className="mt-4 block text-[10px] font-bold uppercase tracking-wider text-white/40">
                      Boot amount
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      value={bootAmount}
                      onChange={(e) => setBootAmount(e.target.value.replace(/[^\d]/g, ''))}
                      className="private-room-modal__field-input mt-1.5 w-full tabular-nums"
                      placeholder={config?.privateTableMinAmount != null ? String(config.privateTableMinAmount) : '100'}
                      disabled={!lobbyVariantMeta.allowed}
                    />
                    {bootPicks.length > 0 && (
                      <div className="private-room-modal__boot-picks">
                        {bootPicks.map((pick) => (
                          <button
                            key={pick}
                            type="button"
                            className={`private-room-modal__boot-pick ${
                              Number(bootAmount) === pick ? 'private-room-modal__boot-pick--active' : ''
                            }`}
                            onClick={() => setBootAmount(String(pick))}
                          >
                            {formatCurrencyAmount('', pick)}
                          </button>
                        ))}
                      </div>
                    )}
                    {config && (config.privateTableMinAmount != null || config.privateTableMaxAmount != null) && (
                      <p className="mt-2 text-[10px] uppercase tracking-wider text-white/35">
                        Range{' '}
                        {config.privateTableMinAmount != null
                          ? formatCurrencyAmount('', config.privateTableMinAmount)
                          : '—'}{' '}
                        –{' '}
                        {config.privateTableMaxAmount != null
                          ? formatCurrencyAmount('', config.privateTableMaxAmount)
                          : '—'}
                      </p>
                    )}
                    <button
                      type="button"
                      disabled={creating || loading || !lobbyVariantMeta.allowed || !user}
                      className="provider-play-btn mt-5 w-full"
                      onClick={() => void createRoom()}
                    >
                      <PlusCircle size={16} />
                      {creating ? 'Creating…' : `Create ${lobbyVariant} room`}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}

interface PrivateRoomLobbyCardProps {
  variantCode: string;
  onOpen: () => void;
}

export function PrivateRoomLobbyCard({ variantCode, onOpen }: PrivateRoomLobbyCardProps) {
  const variantLabel = formatVariantLabel(variantCode);
  const theme = variantTheme(variantCode);
  const ChipIcon = theme === 'purple' ? Crown : Spade;

  return (
    <div id="private-rooms" className="mx-auto mt-5 max-w-xl scroll-mt-20 pb-2 md:pb-1">
      <button type="button" onClick={onOpen} className="private-room-lobby-card group">
        <span className="private-room-lobby-card__shimmer" aria-hidden />
        <span className="flex items-center justify-between gap-3 sm:gap-4">
          <span className="flex min-w-0 items-center gap-3 sm:gap-3.5">
            <span className="private-room-lobby-card__chip">
              <ChipIcon size={18} fill="currentColor" />
            </span>
            <span className="min-w-0 text-left">
              <span className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-tp-gold">
                <DoorOpen size={16} className="shrink-0" />
                Private room
              </span>
              <span className="mt-1 block text-sm leading-snug text-white/55 line-clamp-2 sm:truncate">
                {variantLabel} Teen Patti · tap to create or join with a code
              </span>
            </span>
          </span>
          <Users size={22} className="hidden shrink-0 text-white/30 transition group-hover:text-tp-gold/70 sm:block" />
        </span>
      </button>
    </div>
  );
}
